import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminUsersApi } from '../../api/client'

const THEMES = [{ value: 'light', label: 'Clair' }, { value: 'dark', label: 'Sombre' }]

const DEFAULT_FORM = {
  firstName: '', lastName: '', email: '', password: '',
  jobTitle: '', company: '', phone: '', mobile: '',
  linkedin: '', twitter: '', website: '', bio: '',
  photo: '', primaryColor: '#1a73e8', theme: 'light',
  roles: ['ROLE_USER'],
}

export default function UserFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEdit = Boolean(id)
  const [form, setForm] = useState(DEFAULT_FORM)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const { data: existingUser } = useQuery({
    queryKey: ['admin-user', id],
    queryFn: () => adminUsersApi.get(id).then(r => r.data),
    enabled: isEdit,
  })

  useEffect(() => {
    if (existingUser) {
      setForm({
        ...DEFAULT_FORM,
        ...existingUser,
        password: '',
      })
    }
  }, [existingUser])

  const mutation = useMutation({
    mutationFn: (data) => isEdit
      ? adminUsersApi.update(id, data)
      : adminUsersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setSuccess(isEdit ? 'Profil mis à jour !' : 'Employé créé !')
      setTimeout(() => navigate('/admin/users'), 1500)
    },
    onError: (err) => {
      setError(err.response?.data?.errors || 'Une erreur est survenue.')
    },
  })

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    if (name === 'isAdmin') {
      setForm(f => ({ ...f, roles: checked ? ['ROLE_ADMIN', 'ROLE_USER'] : ['ROLE_USER'] }))
    } else {
      setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }))
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    const data = { ...form }
    if (!data.password) delete data.password
    mutation.mutate(data)
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{isEdit ? 'Éditer l\'employé' : 'Nouvel employé'}</h1>
        <button className="btn btn-outline" onClick={() => navigate('/admin/users')}>← Retour</button>
      </div>

      <form onSubmit={handleSubmit}>
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Informations personnelles</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Prénom *</label>
              <input name="firstName" value={form.firstName} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Nom *</label>
              <input name="lastName" value={form.lastName} onChange={handleChange} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>{isEdit ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe *'}</label>
              <input type="password" name="password" value={form.password} onChange={handleChange} required={!isEdit} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Poste / Titre</label>
              <input name="jobTitle" value={form.jobTitle} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Entreprise</label>
              <input name="company" value={form.company} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label>Photo (URL)</label>
            <input type="url" name="photo" value={form.photo} onChange={handleChange} placeholder="https://..." />
          </div>
          <div className="form-group">
            <label>Biographie</label>
            <textarea name="bio" value={form.bio} onChange={handleChange} rows={3} />
          </div>
        </div>

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Coordonnées</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Téléphone fixe</label>
              <input type="tel" name="phone" value={form.phone} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Mobile</label>
              <input type="tel" name="mobile" value={form.mobile} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label>Site web</label>
            <input type="url" name="website" value={form.website} onChange={handleChange} placeholder="https://..." />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>LinkedIn (URL)</label>
              <input type="url" name="linkedin" value={form.linkedin} onChange={handleChange} placeholder="https://linkedin.com/in/..." />
            </div>
            <div className="form-group">
              <label>Twitter/X (URL)</label>
              <input type="url" name="twitter" value={form.twitter} onChange={handleChange} placeholder="https://x.com/..." />
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Apparence & Permissions</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Couleur principale</label>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <input type="color" name="primaryColor" value={form.primaryColor} onChange={handleChange} style={{ width: 50, height: 40, padding: '2px' }} />
                <input name="primaryColor" value={form.primaryColor} onChange={handleChange} style={{ flex: 1 }} placeholder="#1a73e8" />
              </div>
            </div>
            <div className="form-group">
              <label>Thème</label>
              <select name="theme" value={form.theme} onChange={handleChange}>
                {THEMES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="isAdmin"
                checked={form.roles?.includes('ROLE_ADMIN')}
                onChange={handleChange}
                style={{ width: 'auto' }}
              />
              Accès administrateur
            </label>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <button type="submit" className="btn btn-primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Enregistrement...' : (isEdit ? 'Mettre à jour' : 'Créer l\'employé')}
          </button>
          <button type="button" className="btn btn-outline" onClick={() => navigate('/admin/users')}>
            Annuler
          </button>
        </div>
      </form>
    </div>
  )
}
